import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useHistory} from "react-router-dom";
import MyOpinionLogo from "../../../assets/images/MyOpinion_Logo_Head.png";
import {formatCash, getUserFullName} from "../../../utils/index";

const social = !!process.env.REACT_APP_SOCIAL_HUB;

/** Balance totale en USD (calculée par l'API, `user_balance_usd`). */
const formatUsd = (value) => (value === undefined || value === null
    ? '–'
    : `${Number(value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} USD`);

/**
 * Carte d'un compte (liste /users) : avatar, identité et badges d'état, soldes, compteurs de communauté.
 * Toute la carte ouvre le profil (/users/<nom d'utilisateur>), sauf ses liens et boutons.
 */
const UserCard = ({user}) => {
    const {t} = useTranslation();
    const history = useHistory();
    const profileUrl = `/users/${encodeURIComponent(user?.user_username || '')}`;

    const onClick = (e) => {
        if (!user?.user_username || e.target.closest('a, button')) return;
        if (window.getSelection && String(window.getSelection()).length > 0) return;
        history.push(profileUrl);
    };

    const counters = [
        {icon: 'fa-group', value: (user.friends_invited_count || 0) + (user.friends_accepted_count || 0), label: social ? t("Contacts") : t("Potes")},
        {icon: 'fa-star-o', value: user.followers_count || 0, label: social ? t("Abonnés") : t("Fans")},
        {icon: 'fa-tags', value: user.followed_count || 0, label: social ? t("Abonnements") : t("Fan de")},
        {icon: 'fa-thumbs-o-up', value: user.soutiens_count || 0, label: t("Réactions")},
        {icon: 'fa-pencil', value: user.opinions_count || 0, label: social ? t("Posts") : t("Opinions")},
    ];

    return (
        <div className="card user-card" role="link" tabIndex={0} onClick={onClick}
             onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && history.push(profileUrl)}>
            <div className="user-card__main">
                <img loading="lazy" className="user-card__avatar" src={user?.profile?.prof_picture || MyOpinionLogo} alt="" />
                <div className="user-card__identity">
                    <div className="user-card__name">
                        <span className="text-truncate">{getUserFullName(user)}</span>
                        {user.user_verified ? <i title={t("Certifié")} className="fa fa-check-circle text-primary" /> : null}
                        {user.user_gold ? <i title={t("Premium / Gold")} className="fa fa-star text-warning" /> : null}
                    </div>
                    <div className="user-card__username">@{user?.user_username}</div>
                    {user?.user_email && <div className="user-card__email text-truncate" title={user.user_email}>{user.user_email}</div>}
                    <div className="user-card__badges">
                        {user.user_blocked ? <span className="badge badge-danger ml-0">{t("Bloqué")}</span> : null}
                        {user.user_muted ? <span className="badge badge-warning ml-0"><i className="fa fa-volume-off"></i> {t("Muet")}</span> : null}
                        {user.user_active === false || user.user_active === 0 ? <span className="badge badge-secondary ml-0">{t("Inactif")}</span> : null}
                        {/* user_pays est un identifiant : seul le nom du pays (d'après l'IP) est affiché */}
                        {user?.ip_country?.ip_country_name ? (
                            <span className="badge badge-light border ml-0"><i className="fa fa-map-marker"></i> {user.ip_country.ip_country_name}</span>
                        ) : null}
                    </div>
                </div>
                <Link to={profileUrl} className="btn btn-sm btn-outline-primary user-card__open" title={t("Voir le profil")}>
                    <i className="fa fa-chevron-right"></i>
                </Link>
            </div>

            <div className="user-card__balances">
                <span className="user-card__usd" title={t("Balance totale : pièces, likes et vues non payés aux taux des Paramètres, plus les contenus spéciaux")}>
                    <i className="fa fa-money"></i> {formatUsd(user.user_balance_usd)}
                </span>
                <span><i className="fa fa-database"></i> {formatCash(user.user_wallet_balance) || 0} {t("pièces")}</span>
                <span><i className="fa fa-heart"></i> {formatCash(user.user_like_balance) || 0} {t("likes")}</span>
                <span><i className="fa fa-eye"></i> {formatCash(user.user_view_balance) || 0} {t("vues")}</span>
            </div>

            <div className="user-card__counters">
                {counters.map(item => (
                    <div key={item.label} title={item.label}>
                        <i className={`fa ${item.icon}`}></i>
                        <strong>{formatCash(item.value) || 0}</strong>
                        <small>{item.label}</small>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default UserCard;
